import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, Eye, EyeOff, Pencil, Plus, Trash2, X } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { getCollections, moveCollection, setCollectionVisibility, upsertCollection, deleteCollection } from '../services/collectionService';
import { getProductsAdmin } from '../services/productService';

const AdminCollections = () => {
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [editor, setEditor] = useState(null);

  const refresh = async () => {
    setIsLoading(true);
    try {
      setCollections(getCollections({ includeHidden: true }));
      const loaded = await getProductsAdmin();
      setProducts(Array.isArray(loaded) ? loaded : []);
    } catch (e) {
      toast({ title: 'Failed to load collections', description: String(e?.message || 'Please try again.') });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener('ditris:collections', handler);
    return () => window.removeEventListener('ditris:collections', handler);
  }, []);

  const byId = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  const openCreate = () => {
    setEditor({
      mode: 'create',
      name: '',
      slug: '',
      description: '',
      heroImage: '',
      visible: true,
      productIds: [],
    });
  };

  const openEdit = (c) => {
    setEditor({
      mode: 'edit',
      name: c.name || '',
      slug: c.slug || '',
      description: c.description || '',
      heroImage: c.heroImage || '',
      visible: c.visible !== false,
      productIds: Array.isArray(c.productIds) ? c.productIds : [],
    });
  };

  const save = async () => {
    if (!editor) return;
    const trimmed = String(editor.name || '').trim();
    if (!trimmed) {
      toast({ title: 'Missing name', description: 'Enter a collection name.' });
      return;
    }

    try {
      const saved = await upsertCollection({
        name: trimmed,
        slug: editor.slug,
        description: editor.description,
        heroImage: editor.heroImage,
        productIds: editor.productIds,
        visible: editor.visible,
      });
      toast({ title: 'Saved', description: `${saved.name} updated.` });
      setEditor(null);
      await refresh();
    } catch (e) {
      toast({ title: 'Save failed', description: String(e?.message || 'Please try again.') });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-wide">COLLECTIONS</h1>
          <p className="mt-2 text-sm text-gray-600">Create curated collections (manual product picks for now).</p>
        </div>
        <div>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-lg bg-black text-white px-5 py-3 text-sm font-medium hover:bg-gray-900 transition-colors"
          >
            <Plus size={16} />
            New Collection
          </button>
        </div>
      </div>

      <div className="mt-8 bg-white border border-gray-200 rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="text-xs tracking-widest text-gray-600">COLLECTION LIST</div>
        </div>

        {isLoading ? (
          <div className="px-6 py-10 text-sm text-gray-600">Loading...</div>
        ) : collections.length === 0 ? (
          <div className="px-6 py-10 text-sm text-gray-600">No collections yet.</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {collections.map((c, idx) => (
              <div key={c.slug} className="px-6 py-4 flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-medium text-black">{c.name}</div>
                  <div className="mt-1 text-xs text-gray-600">/{c.slug}</div>
                  {c.description ? <div className="mt-2 text-sm text-gray-700">{c.description}</div> : null}
                  <div className="mt-2 text-xs text-gray-600">{(c.productIds || []).length} product(s)</div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={idx === 0}
                    onClick={() => moveCollection(c.slug, 'up')}
                    className={`inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 hover:bg-gray-50 transition-colors ${
                      idx === 0 ? 'opacity-40 cursor-not-allowed' : ''
                    }`}
                    aria-label="Move up"
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    type="button"
                    disabled={idx === collections.length - 1}
                    onClick={() => moveCollection(c.slug, 'down')}
                    className={`inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 hover:bg-gray-50 transition-colors ${
                      idx === collections.length - 1 ? 'opacity-40 cursor-not-allowed' : ''
                    }`}
                    aria-label="Move down"
                  >
                    <ArrowDown size={16} />
                  </button>

                  <button
                    type="button"
                    onClick={() => setCollectionVisibility(c.slug, c.visible === false)}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 hover:bg-gray-50 transition-colors"
                    aria-label={c.visible === false ? 'Show collection' : 'Hide collection'}
                  >
                    {c.visible === false ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>

                  <button
                    type="button"
                    onClick={() => openEdit(c)}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 hover:bg-gray-50 transition-colors"
                    aria-label="Edit collection"
                  >
                    <Pencil size={16} />
                  </button>

                  <button
                    type="button"
                    onClick={async () => {
                      const ok = window.confirm(`Delete collection “${c.name}”?`);
                      if (!ok) return;
                      await deleteCollection(c.slug);
                    }}
                    className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-white p-2 text-red-700 hover:bg-red-50 transition-colors"
                    aria-label="Delete collection"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editor && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-6 py-10">
          <button type="button" className="absolute inset-0 bg-black/40" onClick={() => setEditor(null)} aria-label="Close" />
          <div className="relative w-full max-w-2xl bg-white rounded-2xl border border-gray-200 shadow-[0_20px_60px_rgba(0,0,0,0.18)] p-6">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium tracking-wide">{editor.mode === 'create' ? 'NEW COLLECTION' : 'EDIT COLLECTION'}</div>
              <button
                type="button"
                onClick={() => setEditor(null)}
                className="rounded-full border border-gray-200 p-2 hover:bg-gray-50 transition-colors"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-800">Name</label>
                <input
                  value={editor.name}
                  onChange={(e) => setEditor((p) => ({ ...p, name: e.target.value }))}
                  className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-black transition-colors"
                  placeholder="e.g. NEW DROP"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800">Slug (optional)</label>
                <input
                  value={editor.slug}
                  onChange={(e) => setEditor((p) => ({ ...p, slug: e.target.value }))}
                  className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-black transition-colors"
                  placeholder="e.g. new-drop"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-800">Description</label>
                <textarea
                  value={editor.description}
                  onChange={(e) => setEditor((p) => ({ ...p, description: e.target.value }))}
                  className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-black transition-colors min-h-[100px]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-800">Hero Image URL (optional)</label>
                <input
                  value={editor.heroImage}
                  onChange={(e) => setEditor((p) => ({ ...p, heroImage: e.target.value }))}
                  className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-black transition-colors"
                  placeholder="https://..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-800">Products</label>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-auto rounded-lg border border-gray-200 p-3">
                  {products.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={(editor.productIds || []).includes(p.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setEditor((prev) => {
                            const current = Array.isArray(prev.productIds) ? prev.productIds : [];
                            return {
                              ...prev,
                              productIds: checked ? Array.from(new Set([...current, p.id])) : current.filter((x) => x !== p.id),
                            };
                          });
                        }}
                      />
                      {p.name || p.id}
                    </label>
                  ))}
                </div>
                <div className="mt-2 text-xs text-gray-600">Selected: {(editor.productIds || []).length}</div>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!editor.visible}
                    onChange={(e) => setEditor((p) => ({ ...p, visible: e.target.checked }))}
                  />
                  Visible
                </label>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={save}
                className="flex-1 rounded-lg bg-black text-white px-4 py-3 text-sm font-medium hover:bg-gray-900 transition-colors"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => setEditor(null)}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCollections;
